import { forwardRef, useImperativeHandle, useRef, useState } from "react";
import { FaSave } from "react-icons/fa";
import * as Yup from "yup";

import { ModalHandles, Modal } from "../../../../../../components/global/modal";
import {
    SemvInput,
    SemvInputHandle,
} from "../../../../../../components/ui/input";
import { ApiErrorHandler } from "../../../../../../shared/utils/errors.utils";
import { yupValidation } from "../../../../../../shared/utils/yup";
import { semvApi } from "../../../../../../shared/services/axios/apis.service";
import { Secretary } from "../types/types";
import { showToast } from "../../../../../../components/global/toast";
import { useInvalidateCache } from "../../../../../../shared/hooks/useInvalidateCache";

export type UpsertSecretaryModalHandles = {
    open: (secretary?: Secretary) => void;
    close: () => void;
};

const schema = Yup.object({
    name: Yup.string().required("Nome da obrigatório."),
});

const UpsertSecretaryModal = forwardRef<UpsertSecretaryModalHandles>(
    (_, ref) => {
        const invalidate = useInvalidateCache();

        const [loading, setLoading] = useState(false);
        const [secretary, setSecretary] = useState<Secretary>();
        const innerRef = useRef<ModalHandles>(null);
        const nameRef = useRef<SemvInputHandle>(null);

        const isUpdate = !!secretary;

        const handleClose = () => {
            setSecretary(undefined);
            innerRef.current?.close();
        };

        useImperativeHandle(ref, () => ({
            close: handleClose,
            open: (secretary$) => {
                setSecretary(secretary$);
                innerRef.current?.open();
            },
        }));

        const handleSubmit = async () => {
            try {
                setLoading(true);
                const formData = { name: nameRef.current?.getValue() };

                await schema.validate(formData, {
                    abortEarly: false,
                });

                if (isUpdate) {
                    await semvApi.patch(
                        `/government-plan/secretary/${secretary.id}`,
                        formData
                    );
                } else {
                    await semvApi.post(`/government-plan/secretary`, formData);
                }

                showToast({
                    type: "success",
                    message: isUpdate
                        ? "Secretaria atualizada com sucesso"
                        : "Secretaria cadastrada com sucesso ",
                });

                invalidate();
                handleClose();
            } catch (err) {
                ApiErrorHandler(err);

                const validationErrors = yupValidation(err);

                nameRef.current?.setError(validationErrors.name);
            } finally {
                setLoading(false);
            }
        };

        return (
            <Modal
                ref={innerRef}
                title={`${
                    isUpdate ? "Atualiazação" : "Cadastro"
                } de secretaria`}
                size="md"
                handleCloseModal={() => setSecretary(undefined)}
            >
                <form
                    onSubmit={(e) => {
                        e.preventDefault();
                        handleSubmit();
                    }}
                >
                    <SemvInput
                        label="Nome"
                        name="name"
                        placeholder="Digite o nome"
                        defaultValue={secretary?.name || ""}
                        ref={nameRef}
                    />
                    <button
                        type="submit"
                        className="btn-primary flex space-x-2 items-center ml-auto mt-2"
                        disabled={loading}
                    >
                        <span>{isUpdate ? "Atualizar" : "Cadastrar"}</span>
                        <FaSave />
                    </button>
                </form>
            </Modal>
        );
    }
);

UpsertSecretaryModal.displayName = "UpsertSecretaryModal";

export { UpsertSecretaryModal };
